import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Select, SelectTrigger } from "@activecollab/components";
import PropTypes from "prop-types";
import { ProjectsPickerGroupBy } from "~/components/ProjectsPickerGroupBy/ProjectsPickerGroupBy";
import { useProjectsForPeopleInvitation } from "~/repository/useProjectsForPeopleInvitation";
import { editPersonalizedConfigOptions } from "~/store/actions/actions.personalizedConfigOptions";
import { getAllCompanies } from "~/store/selectors/selector.companies";
import { getRawPersonalizedConfigOption } from "~/store/selectors/selector.configOptions";
import { getFavoriteProjectIds } from "~/store/selectors/selector.favorites";
import { getAllProjectLabels } from "~/store/selectors/selector.labels";
import { getProjectCategories } from "~/store/selectors/selector.projectCategories";
import { getAllUsers } from "~/store/selectors/selector.users";

export const SelectProjects = ({ id, name, value, placeholder, onChange }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const companies = useSelector(getAllCompanies);
  const projectLabels = useSelector(getAllProjectLabels);
  const categories = useSelector(getProjectCategories);
  const leaders = useSelector(getAllUsers);
  const favoriteProjectIds = useSelector(getFavoriteProjectIds);
  const projectsPickerGroupBy = useSelector((state) =>
    getRawPersonalizedConfigOption(state, "projects_picker_group_by")
  );

  const label = placeholder ? placeholder : t("Any Project");

  const { data: filteredAndSortedProjects, isFetched } = useProjectsForPeopleInvitation();

  const handleChangeGroupBy = useCallback(
    (value) => {
      dispatch(editPersonalizedConfigOptions({ projects_picker_group_by: value }));
    },
    [dispatch]
  );

  const projectsForSelect = useMemo(() => {
    let groupBy = projectsPickerGroupBy;

    const config = {
      labels: {
        field: "label_id",
        objects: projectLabels?.toJS() || [],
        other: t("Without Label"),
      },
      companies: {
        field: "company_id",
        objects: companies || [],
        other: t("Without Client"),
      },
      categories: {
        field: "category_id",
        objects: categories || [],
        other: t("Without Category"),
      },
      leaders: {
        field: "leader_id",
        objects: leaders?.toJS() || [],
        other: t("Without Leader"),
      },
      favorites: { objects: favoriteProjectIds, other: t("Other") },
    };

    if (config[groupBy] === undefined) {
      groupBy = "favorites";
    }

    if (isFetched) {
      let groups = filteredAndSortedProjects?.reduce((acc, item) => {
        let group = item[config[groupBy].field] || 0;

        if (groupBy === "favorites" && favoriteProjectIds.includes(item.id)) {
          group = "favorites";
        }

        acc[group] = [...(acc[group] || []), { id: item.id, name: item.name }];

        return acc;
      }, {});

      groups = Object.entries(groups)
        .map(([id, options]) => {
          options.sort((a, b) => a.name.localeCompare(b.name));

          let name = config[groupBy].objects[id]?.name;
          if (groupBy === "leaders") {
            name = config[groupBy].objects[parseInt(id)]?.display_name;
          }

          name = name || config[groupBy].other;

          if (id === "favorites") {
            name = t("Favorite");
          }

          return { id: name, name, options };
        })
        .sort((a, b) => a.name.localeCompare(b.name));

      const index = groups.findIndex((item) => item.id === 0);

      if (index !== -1) groups.push(groups.splice(index, 1)[0]);

      return groupBy === "favorites" && groups.length === 1 ? groups[0].options : groups;
    }
  }, [
    categories,
    companies,
    favoriteProjectIds,
    filteredAndSortedProjects,
    isFetched,
    leaders,
    projectLabels,
    projectsPickerGroupBy,
    t,
  ]);

  const handleChange = useCallback(
    (_value) => {
      if (typeof onChange === "function") {
        onChange(_value);
      }
    },
    [onChange]
  );

  return (
    <>
      <div className="tw-flex tw-w-full tw-flex-col">
        <div className="tw-flex tw-w-full tw-flex-col">
          <Select
            target={
              <SelectTrigger
                id={id}
                name={name}
                className="tw-w-full tw-max-w-[360px]"
                typographyProps={{
                  color: value?.length > 0 ? "primary" : "tertiary",
                }}
              >
                {value.length === 0 ? label : t("{{count}} Project", { count: value.length })}
              </SelectTrigger>
            }
            type="multiple"
            options={projectsForSelect}
            selected={value}
            className="tw-w-full tw-shrink-0"
            onChange={handleChange}
            noResultText={t("No results")}
            endAdornment={
              <ProjectsPickerGroupBy
                onChange={handleChangeGroupBy}
                selected={projectsPickerGroupBy}
              />
            }
          />
        </div>
      </div>
    </>
  );
};

SelectProjects.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.array,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
};
